import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Shield, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import { authApi } from '../api/auth'

export function SetupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [form, setForm] = useState({
    password: '',
    confirmPassword: '',
    email: '',
  })
  const [error, setError] = useState<string | null>(null)

  const setupMutation = useMutation({
    mutationFn: authApi.initialSetup,
    onSuccess: () => {
      // Redirect to login after successful setup
      navigate('/login', { state: { setupComplete: true } })
    },
    onError: (err: Error) => {
      setError(err.message || t('setup.error'))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match
    if (form.password !== form.confirmPassword) {
      setError(t('setup.passwordsDontMatch'))
      return
    }

    // Validate password strength
    if (form.password.length < 8) {
      setError(t('setup.passwordTooShort'))
      return
    }

    setupMutation.mutate({
      password: form.password,
      confirmPassword: form.confirmPassword,
      email: form.email || undefined,
    })
  }

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { level: 0, label: '' }
    let strength = 0
    if (password.length >= 8) strength++
    if (password.length >= 12) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++

    if (strength <= 2) return { level: 1, label: t('setup.passwordWeak'), color: 'bg-red-500' }
    if (strength <= 3) return { level: 2, label: t('setup.passwordMedium'), color: 'bg-yellow-500' }
    return { level: 3, label: t('setup.passwordStrong'), color: 'bg-green-500' }
  }

  const passwordStrength = getPasswordStrength(form.password)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 text-balance">{t('setup.title')}</h1>
          <p className="text-gray-400">{t('setup.subtitle')}</p>
        </div>

        {/* Setup Form */}
        <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 p-8">
          <div className="mb-6 p-4 bg-teal-900/30 border border-teal-700 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-teal-300 font-medium">{t('setup.welcomeTitle')}</p>
                <p className="text-teal-400/80 text-sm mt-1">{t('setup.welcomeMessage')}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('setup.email')} <span className="text-gray-500">({t('setup.optional')})</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@company.com"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:border-transparent"
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('setup.password')} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:border-transparent pr-12"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Password strength indicator */}
              {form.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded ${
                          level <= passwordStrength.level ? passwordStrength.color : 'bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${
                    passwordStrength.level === 1 ? 'text-red-400' :
                    passwordStrength.level === 2 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('setup.confirmPassword')} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className={`w-full px-4 py-3 bg-gray-900 border rounded-lg text-white placeholder-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:border-transparent pr-12 ${
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? 'border-red-500'
                      : 'border-gray-700'
                  }`}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="mt-1 text-xs text-red-400">{t('setup.passwordsDontMatch')}</p>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={setupMutation.isPending || !form.password || !form.confirmPassword}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
            >
              {setupMutation.isPending ? t('setup.configuring') : t('setup.configure')}
            </button>
          </form>

          {/* Security tips */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <p className="text-gray-400 text-xs">
              {t('setup.securityTip')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
